import { DateUtils } from '@broker/utils';
import { DownloadApp } from 'components/News/DownloadApp';
import { BasePath } from 'constants/basePath';
import { useEffect, useRef, useState } from 'react';
import { BlogType, EventType, NewsType } from 'types';
import { AppEnv } from 'utils/accessEnviroment';

import { ImagePreset, ReadingProgress } from '@dnse/ui';

import { BreadCrumbs } from '../BreadCrumbs';
import { FloatShare } from './FloatShare';
import PostAuthor from './PostAuthor';
import { PostScrollSpy } from './PostScrollSpy';
import { RelatedPosts } from './RelatedPost';
import ScrollToTopBtn from './ScrollToTopBtn';
import Css from './post.module.scss';
import { getDirectusImg } from '../../../services/graphql/directus';


interface Props {
  absoluteUrl: string;
  post: NewsType | EventType | BlogType;
  relatedPosts: NewsType[] | EventType[] | BlogType[];
  totalCountRelatedPosts: number;
  totalCountPostsByUserID: number;
}

const PostDetails: React.FC<Props> = ({
                                        post,
                                        absoluteUrl,
                                        relatedPosts,
                                        totalCountRelatedPosts,
                                        totalCountPostsByUserID
                                      }) => {
  const target = useRef<HTMLDivElement>(null);
  const [isOpenMenu, setIsOpenMenu] = useState(false);
  const [isShowMenu, setIsShowMenu] = useState(true);

  const basePath =
    post.__typename === 'blogs'
      ? '/blog'
      : post.__typename === 'news'
        ? '/happy-dnse/tin-tuc'
        : '/happy-dnse/su-kien';

  const postURL = `${AppEnv.HOST}${basePath}/${post.slug}`;
  const breadcrumbItems =
    post.__typename === 'blogs'
      ? [
        {
          to: '/blog',
          label: 'Blog',
          isNativeLink: true
        },
        {
          to: '/blog',
          label: 'Bài viết'
        }
      ]
      : [
        {
          to: basePath,
          label: 'Happy DNSE',
          isNativeLink: true
        },
        {
          to:
            post?.__typename === 'news'
              ? BasePath.NewsBasePath
              : BasePath.EventsBasePath,
          label:
            post?.__typename === 'news'
              ? 'Tin tức về DNSE'
              : 'Sự kiện nổi bật'
        }
      ];

  useEffect(() => {
    const headings = document.querySelector("#post-content").querySelectorAll('h2,h3');
    setIsShowMenu(headings.length > 0)
  }, [])
  return (
    <div className="flex justify-center mt-[64px] scroll-smooth">
      <div className="max-w-[1120px] px-4 md:px-0 pt-3">
        <ReadingProgress target={target}/>
        <ScrollToTopBtn/>
        <BreadCrumbs items={breadcrumbItems}/>
        {
          !isShowMenu && <div>
            <ImagePreset src={getDirectusImg(post.cover_image)} className="mt-6" />
          </div>
        }
        <div className={`flex mt-8 ${isShowMenu ? '' : 'justify-center'} relative`}>
          {
            isShowMenu &&
            <aside
              className="relative md:basis-1/3 hidden md:block pr-2"
              style={{ contain: 'paint' }}
            >
              <div className="sticky top-[100px]">
                <div>
                  <PostScrollSpy id="#post-content" slug={post.slug} />
                </div>
              </div>
            </aside>
          }

          <div className="basis-full md:basis-[70%] max-w-[calc(100vw-32px)] md:max-w-[70%] ">
            <article data-testid="post-content">
              <p className="text-gray-700 font-normal text-sm mb-2">
                {DateUtils.formatDate(post.date_created, 'HH:MM, DD/MM/YYYY')}
              </p>
              <div ref={target}>
                <h1 className="text-2xl mb-5 font-bold">{post.title}</h1>
                <div
                  id="post-sapo"
                  className={`mx-auto ${Css['post-content']} mb-3`}
                >
                  {post.sapo}
                </div>
                <div className="block md:hidden my-2">
                  <FloatShare
                    title={post.title}
                    href={absoluteUrl}
                    postUrl={postURL}
                  />
                </div>

                <div
                  id="post-content"
                  dangerouslySetInnerHTML={{
                    __html: post.content
                  }}
                  className={`mx-auto ${Css['post-content']}`}
                />
              </div>
            </article>
            <div className="mt-2 mb-6 flex flex-col gap-6">
              <div className="block md:hidden">
                <FloatShare
                  title={post.title}
                  href={absoluteUrl}
                  postUrl={postURL}
                />
              </div>
              <PostAuthor
                author={post.user_created}
                totalCountPostsByUserID={totalCountPostsByUserID}
              />
              <div className="w-full h-[1px] bg-gray-950" />
            </div>
            <RelatedPosts
              originPost={post}
              postType={post.__typename}
              relatedPosts={relatedPosts}
              totalCountRelatedPosts={totalCountRelatedPosts}
            />
            <DownloadApp />
          </div>

          <aside className={isShowMenu ? "relative hidden md:block " : "hidden md:absolute right-0 h-full top-o"}>
            <div className="sticky top-[100px] pl-9">
              <FloatShare
                title={post?.title}
                href={absoluteUrl}
                postUrl={postURL}
              />
            </div>
          </aside>

          {!isOpenMenu && isShowMenu && (
            <aside className="relative block md:hidden w-0">
              <button
                id="mobile-scrollspy-menu-button"
                className="fixed right-0 w-[60px] h-[50px] px-4 py-3 bg-white shadow-xl"
                style={{ borderRadius: '32px 0 0 32px' }}
                onClick={() => setIsOpenMenu(true)}
              >
                <div>
                  <img src={'/svg/icon-menu-2.svg'} alt="menu" />
                </div>
              </button>
            </aside>
          )}
        </div>
      </div>
      {isOpenMenu && (
        <div
          className="fixed block md:hidden z-20 bottom-0 w-full shadow-[0_-5px_15px_rgba(0,0,0,0.3)] bg-white/40 before:content-[''] before:absolute before:-z-1 before:backdrop-blur-3xl before:inset-0"
          style={{
            borderRadius: '4px 4px 0 0'
          }}
        >
          <div>
            <PostScrollSpy
              id="#post-content"
              slug={post.slug}
              onClose={() => setIsOpenMenu(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PostDetails;
